'use client';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import { ReactNode, useEffect, useState } from 'react';
import CustomRichText from '../CustomRichText';

export interface StudyFormatBlockProps {
  title: ReactNode | SerializedEditorState;
  subtitle: ReactNode | SerializedEditorState;
  txtContent: ReactNode | SerializedEditorState;
  index: number;
  color: 'violet' | 'green' | 'blue';
}

export default function StudyFormatCard({
  title,
  subtitle,
  txtContent,
  index,
  color,
}: StudyFormatBlockProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false); // Додано для перевірки видимості

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1023); // Визначаємо, чи мобільний екран
    };

    const handleScroll = () => {
      if (isMobile) {
        // Визначаємо видимість картки через Intersection Observer
        const observer = new IntersectionObserver(
          entries => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                setIsVisible(true);
              } else {
                setIsVisible(false);
              }
            });
          },
          {
            threshold: 0.9, // Картка вважається видимою, якщо 90% її висоти видно
          },
        );

        const cardElement = document.getElementById(`card-${index}`);
        if (cardElement) {
          observer.observe(cardElement);
        }

        return () => {
          if (cardElement) {
            observer.unobserve(cardElement);
          }
        };
      }
    };

    // Відстежуємо зміну розміру вікна
    window.addEventListener('resize', handleResize);

    // Перевіряємо стан на початку завантаження
    handleResize();
    handleScroll();

    // Очищаємо слухачі подій
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile, index]);

  const backgroundColorClass =
    {
      green: {
        border: 'file-border-green',
        bgHover: 'file-green',
        line: 'border-green-300',
        width: 'w-[350px]',
        height: 'h-[550px]',
      },
      blue: {
        border: 'file-border-blue',
        bgHover: 'file-blue',
        line: 'border-blue-300',
        width: 'w-full',
        height: 'h-[300px]',
      },
      violet: {
        border: 'file-border-violet',
        bgHover: 'file-violet',
        line: 'border-violet-300',
        width: 'w-[350px]',
        height: 'h-[350px]',
      },
    }[color] || 'custom-gradient-green';

  return (
    <div
      id={`card-${index}`} // Додано ID для картки
      data-meta="StudyFormatCard"
      className={`overflow-hidden text-white ${
        isMobile && isVisible && window.scrollY > 100 // Перевірка видимості картки
          ? backgroundColorClass.bgHover
          : backgroundColorClass.border
      } ${backgroundColorClass.width} ${
        backgroundColorClass.height
      } flex flex-col transition-all duration-500 ease-in-out hover:${
        backgroundColorClass.bgHover
      }`}
    >
      <div className={`px-8 pt-4 ${color === 'violet' ? 'pb-16' : 'pb-4'} max-w-[350px]`}>
        <div className={`pb-4 border-b ${backgroundColorClass.line} h-20 mb-2`}>
          <CustomRichText data={title} tag="div" className="text-xl font-bold" />
          <CustomRichText data={subtitle} tag="div" className="text-xs" />
        </div>
        <CustomRichText data={txtContent} tag="div" className="text-xs sm:text-sm" />
      </div>
    </div>
  );
}
